import { useEffect, useState } from 'react';
import { UnitStateData } from '@mark43/cad-api';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import {
    BlankModal,
    ButtonGroup,
    HStack,
    ModalBody,
    ModalFooter,
    VStack,
    Button,
    Radio,
    InlineBanner,
    Text,
    Box,
} from '@cad-arc';
import { useFullscreenModalControls } from '../modals/context/modal-context-provider';
import { useUnitAssignment } from '../unit-assignment/unit-assignment-context';
import { useUnitAssignSelfToEventMutation } from '../unit-assignment/use-unit-assign-self-to-event-mutation';
import { useEnRouteStates } from '../common/hooks/use-en-route-states';
import { useNavigate } from 'react-router-dom';
import { ROOT_PATH } from '~/router/constants';

export type UnitSelfAssignmentModalComponentType = typeof UnitSelfAssignmentModal;

export interface SelfAssignmentEvent {
    eventId?: number;
    eventNumber?: string;
    eventLocation?: string;
    assignedUnits?: unknown[];
}

export interface SelfAssignmentEvents {
    selectedEvent?: SelfAssignmentEvent;
    assignedEvent?: SelfAssignmentEvent;
}

export function UnitSelfAssignmentModal({
    selectedEvent,
    assignedEvent,
}: SelfAssignmentEvents): React.ReactElement {
    const { heldEvents, automaticallyHoldEventsForUnits } = useEnabledFeatures();
    const [holdEventForPreemptUnit, setHoldEventForPreemptUnit] = useState(
        automaticallyHoldEventsForUnits
    );
    const unitAssignment = useUnitAssignment();
    const modalControls = useFullscreenModalControls();
    const { enRouteState, alternateEnRouteState } = useEnRouteStates();
    const unitAssignSelfToEventMutation = useUnitAssignSelfToEventMutation({
        holdEventForPreemptUnit,
    });
    const unitId = unitAssignment?.unitId;

    const assignedEventId = unitAssignment?.assignedEvent?.eventId;
    const isUnitAlreadyAssigned = !!unitAssignment?.assignedEvent;
    const isTheOnlyOneAssignedUnitToEvent = assignedEvent?.assignedUnits?.length === 1;

    const navigate = useNavigate();

    const assignEventToUnitState = (unitStateData: UnitStateData) => {
        if (unitId) {
            unitAssignSelfToEventMutation
                // @ts-expect-error types for the endpoint are wrong
                .mutateAsync({
                    agencyEventId: selectedEvent?.eventId,
                    unitStateId: unitStateData.id,
                    unitId,
                })
                .then((transitioned) => {
                    if (transitioned) {
                        modalControls.close();
                        navigate(ROOT_PATH);
                    }
                });
        }
    };

    useEffect(
        function checkIfUnitIsAssignedToSelectedEventFromAnotherPlace() {
            if (assignedEventId === selectedEvent?.eventId) {
                modalControls.close();
            }
        },
        [assignedEventId, modalControls, selectedEvent?.eventId]
    );

    const errorMessage = unitAssignSelfToEventMutation.error?.response?.data?.error;

    const showModalOptions =
        isUnitAlreadyAssigned &&
        isTheOnlyOneAssignedUnitToEvent &&
        heldEvents &&
        !automaticallyHoldEventsForUnits;

    return (
        <BlankModal isOpen hideCloseButton size="lg" onClose={() => modalControls.close()}>
            <ModalBody>
                {!!errorMessage && <InlineBanner title={errorMessage} status="error" />}
                <Box marginBottom={4}>
                    {!!selectedEvent && (
                        <Text>
                            Assigning yourself to {selectedEvent.eventId} (
                            {selectedEvent.eventLocation}).
                        </Text>
                    )}
                    {isUnitAlreadyAssigned && isTheOnlyOneAssignedUnitToEvent && (
                        <Text>
                            EVENT {assignedEvent?.eventId} ({assignedEvent.eventLocation}) will
                            become unassigned.
                        </Text>
                    )}
                </Box>
                {showModalOptions && (
                    <VStack align="left" marginBottom={4}>
                        <Radio
                            isChecked={!holdEventForPreemptUnit}
                            onChange={() => setHoldEventForPreemptUnit(false)}
                            data-testid="MODAL_OPTION__ASSIGN_ONLY"
                        >
                            Assign Only
                        </Radio>
                        <Radio
                            isChecked={holdEventForPreemptUnit}
                            onChange={() => setHoldEventForPreemptUnit(true)}
                            data-testid="MODAL_OPTION__ASSIGN_HOLD"
                        >
                            Assign and Hold
                        </Radio>
                    </VStack>
                )}
                <Text>Change status to:</Text>
            </ModalBody>
            <ModalFooter>
                <HStack width="100%" alignItems="center" justifyContent="space-between">
                    <ButtonGroup>
                        {!!enRouteState && (
                            <Button
                                variant="solid"
                                isLoading={unitAssignSelfToEventMutation.isLoading}
                                onClick={() => {
                                    assignEventToUnitState(enRouteState);
                                }}
                            >
                                {enRouteState.value}
                            </Button>
                        )}
                        {!!alternateEnRouteState && (
                            <Button
                                variant="outline"
                                isLoading={unitAssignSelfToEventMutation.isLoading}
                                onClick={() => {
                                    assignEventToUnitState(alternateEnRouteState);
                                }}
                            >
                                {alternateEnRouteState.value}
                            </Button>
                        )}
                    </ButtonGroup>
                    <Button variant="ghost" onClick={() => modalControls.close()}>
                        Cancel
                    </Button>
                </HStack>
            </ModalFooter>
        </BlankModal>
    );
}
